import * as members from '../../privates/privateMembers';
import {validate} from '../../privates/validations/validation';

/**
 * @mixin ViewportMixin
 * @summary Provides functionality for elements that can be scrolled into and out
 *  of view.
 * @description 
 *  `ViewportMixin` is a mixin that provides event handlers for elements on the page 
 *  that can be scrolled into and out of view.     
 *  
 *  **About Mixins**
 * 
 *  [Mixins](https://support.wix.com/en/article/velo-about-mixins) provide functionality that other elements can inherit and use. 
 *  
 *  Mixins are not elements. You cannot add mixins to a page in the Editor like other `$w` elements, and mixins are not meant to be used directly 
 *  in your code. 
 *   
 *  For example, you would not write code like this, because it is out of context: 
 * 
 *  ```javascript
 *  $w("#ViewportMixin").onViewportEnter( (event) => {
 *     let targetId = event.target.id; 
 *  });
 *  ```
 * 
 *  Instead, you can code the following if `myElement` is an element that 
 *  "mixes in" `ViewportMixin`. 
 * 
 *  ```javascript
 *  $w("#myElement").onViewportEnter( (event) => {
 *     let targetId = event.target.id; 
 *  });
 *  ```
 *  
 * @memberof $w
 */
class ViewportMixin {

    /**
     * @function onViewportEnter
     * @syntax
     * function onViewportEnter(handler: EventHandler): Element
     * callback EventHandler(event: Event, $w: Function): void
     * @summary Adds an event handler that runs when an element is displayed
     *  in the viewable part of the current window.
     * @description
     *  An element enters the viewport when the page is scrolled to show any
     *  part of the element. An element also enters the viewport if it was
     *  [hidden]($w.HiddenMixin.html#hidden) or [collapsed]($w.CollapsedMixin.html#collapsed)
     *  and is then shown or expanded in the viewable part of the current window. `onViewportEnter()` 
     *  is not fired for [hidden]($w.HiddenMixin.html#hidden) or [collapsed]($w.CollapsedMixin.html#collapsed) 
     *  elements even if they are scrolled into view.
     * 
     *  You can also [define an event handler using the Properties and Events panel](https://support.wix.com/en/article/velo-reacting-to-user-actions-using-events).
     * 
     * 
     * > **Deprecation note:** The $w parameter of event handlers is being deprecated. To get
     *  a scoped selector for working with elements in repeater items, use the [$w.at()]($w.html#at) function
     *  and pass it the context property of the event parameter: `$item = $w.at(event.context)`. To learn more, see
     *  [here](https://www.wix.com/velo/forum/wix-tips-and-updates/removal-of-the-w-parameter-from-event-handlers).
     *
     * @param {$w.EventHandler} handler The name of the function or
     *  the function expression to run when the element enters the viewport.
     * @returns {$w.Element} The element on which the event is now registered.
     * @snippet [ViewportMixin-onViewportEnter.es6=Get the ID of the element that has entered the viewport]
     * @see [onViewportLeave( )](#onViewportLeave)
     * @memberof $w.ViewportMixin
     * @eventtype viewportEnter
     * @eventhandler
     */
    
    onViewportEnter(cb) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'onViewportEnter',
            dataToValidate: {onViewportEnter: cb},
            compName: this.id
        })) {
            return this;
        }
        const RMI = members._RMI.get(this);
        const contextId = members._contextId.get(this);
        const controllerId = members._controllerId.get(this);
        const compId = members._compId.get(this);
        RMI.registerEvent(contextId, compId, RMI.getEventTypes().VIEWPORT_ENTER, controllerId, cb);
        return this;
    }

    /**
     * @function onViewportLeave
     * @syntax
     * function onViewportLeave(handler: EventHandler): Element
     * callback EventHandler(event: Event, $w: Function): void
     * @summary Adds an event handler that runs when an element is no longer
     *  displayed in the viewable part of the current window.
     * @description
     *  An element leaves the viewport when the page is scrolled so that the
     *  element is completely out of view. An element also leaves the viewport
     *  if it was shown or expanded and is then [hidden]($w.HiddenMixin.html#hidden)
     *  or [collapsed]($w.CollapsedMixin.html#collapsed) from the viewable part
     *  of the current window.  `onViewportLeave()` 
     *  is not fired for [hidden]($w.HiddenMixin.html#hidden) or [collapsed]($w.CollapsedMixin.html#collapsed) 
     *  elements even if they are scrolled out of view.
     * 
     *  You can also [define an event handler using the Properties and Events panel](https://support.wix.com/en/article/velo-reacting-to-user-actions-using-events).
     * 
     * 
     * > **Deprecation note:** The $w parameter of event handlers is being deprecated. To get
     *  a scoped selector for working with elements in repeater items, use the [$w.at()]($w.html#at) function
     *  and pass it the context property of the event parameter: `$item = $w.at(event.context)`. To learn more, see
     *  [here](https://www.wix.com/velo/forum/wix-tips-and-updates/removal-of-the-w-parameter-from-event-handlers).
     * @param {$w.EventHandler} handler The name of the function or
     *  the function expression to run when the element leaves the viewport.
     * @returns {$w.Element} The element on which the event is now registered.
     * @snippet [ViewportMixin-onViewportLeave.es6=Get the ID of the element that has left the viewport]
     * @see [onViewportEnter( )](#onViewportEnter)
     * @memberof $w.ViewportMixin
     * @eventtype viewportLeave
     * @eventhandler
     */
    onViewportLeave(cb) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'onViewportLeave',
            dataToValidate: {onViewportLeave: cb},
            compName: this.id
        })) {
            return this;
        }
        const RMI = members._RMI.get(this);
        const contextId = members._contextId.get(this);
        const controllerId = members._controllerId.get(this);
        const compId = members._compId.get(this);
        RMI.registerEvent(contextId, compId, RMI.getEventTypes().VIEWPORT_LEAVE, controllerId, cb);
        return this;
    }
}

export default ViewportMixin;
